import { paymentProcessingApiBaseUrl } from '../../shared/env';
import { apiErrorFromResponse } from '../../shared/apiErrorFromResponse';
import { createJsonClient } from '../../shared/httpClient';
import { refreshAccessToken } from '../../shared/tokenRefresh';
import type { ContractorPaymentRecordsGateway } from './ContractorPaymentRecordsGateway';
import { queryString } from './query';
import type {
  ExportFormat, ExportedFile, OutsidePaymentInput, PaymentRecordDetail, PaymentRecordPage, PaymentRecordSummary, PaymentStatement, RecordQuery,
} from './types';

const BASE = '/api/contractor-payment-records';
const enc = encodeURIComponent;

function fileNameFromDisposition(header: string | null, fallback: string): string {
  const match = header ? /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header) : null;
  const raw = match?.[1];
  if (!raw) return fallback;
  try {
    return decodeURIComponent(raw).replace(/[\\/]/g, '_');
  } catch {
    return fallback;
  }
}

export class HttpContractorPaymentRecordsGateway implements ContractorPaymentRecordsGateway {
  private readonly request = createJsonClient(paymentProcessingApiBaseUrl);

  list(query: RecordQuery, signal?: AbortSignal): Promise<PaymentRecordPage> {
    return this.request(`${BASE}${queryString(query)}`, undefined, signal);
  }

  summary(year: number, signal?: AbortSignal): Promise<PaymentRecordSummary> {
    return this.request(`${BASE}/summary?year=${year}`, undefined, signal);
  }

  get(id: string, signal?: AbortSignal): Promise<PaymentRecordDetail> {
    return this.request(`${BASE}/${enc(id)}`, undefined, signal);
  }

  statement(id: string, signal?: AbortSignal): Promise<PaymentStatement> {
    return this.request(`${BASE}/${enc(id)}/statement`, undefined, signal);
  }

  markPaid(id: string, paidOn: string, reference: string | null): Promise<PaymentRecordDetail> {
    return this.request(`${BASE}/${enc(id)}/mark-paid`, { method: 'POST', body: JSON.stringify({ paidOn, ...(reference ? { reference } : {}) }) });
  }

  voidRecord(id: string, reason: string): Promise<PaymentRecordDetail> {
    return this.request(`${BASE}/${enc(id)}/void`, { method: 'POST', body: JSON.stringify({ reason }) });
  }

  recordOutside(input: OutsidePaymentInput): Promise<PaymentRecordDetail> {
    const form = new FormData();
    form.set('contractorId', input.contractorId);
    form.set('paymentDate', input.paymentDate);
    form.set('paymentMethod', input.paymentMethod);
    form.set('servicePeriodStart', input.servicePeriodStart);
    form.set('servicePeriodEnd', input.servicePeriodEnd);
    form.set('amount', String(input.amount));
    if (input.memo.trim()) form.set('memo', input.memo.trim());
    if (input.reference.trim()) form.set('reference', input.reference.trim());
    if (input.paymentMethod === 'Check' && input.checkNumber.trim()) form.set('checkNumber', input.checkNumber.trim());
    form.set('form1099Trackable', String(input.form1099Trackable));
    form.set('recordAnyway', String(input.recordAnyway));
    if (input.file) form.set('file', input.file);
    return this.request(`${BASE}/outside`, { method: 'POST', body: form });
  }

  async exportRecords(format: ExportFormat, query: RecordQuery): Promise<ExportedFile> {
    const path = `${BASE}/export${queryString({ ...query, format } as Partial<RecordQuery>, true)}`;
    const url = `${paymentProcessingApiBaseUrl}${path}`;
    const doFetch = () => fetch(url, { method: 'GET', credentials: 'include' });
    let response = await doFetch();
    if (response.status === 401 && (await refreshAccessToken())) response = await doFetch();
    if (!response.ok) throw await apiErrorFromResponse(response, `Request to ${BASE}/export failed with ${response.status}`);
    const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    return { blob: await response.blob(), fileName: fileNameFromDisposition(response.headers.get('Content-Disposition'), `contractor-payments-${stamp}.${format}`) };
  }
}
